import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import PublicIcon from '@mui/icons-material/Public';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WineBarIcon from '@mui/icons-material/WineBar';
import {
  Box,
  CircularProgress,
  Paper,
  Tooltip as MuiTooltip,
  Typography,
  type TooltipProps,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  useStatePerformance,
  type StateMomentum,
  type StatePerformanceItem,
  type StateYearlyVolume,
  type DateRange,
} from '../api/depletions';
import { useClickOutside } from '../hooks/useClickOutside';
import { colors } from '../theme';
import { classifyState, isControlState } from '../lib/controlStates';
import { fmt6PkNumber } from '../lib/volume';
import { ControlStateChip } from './ControlStateChip';
import { PremisesBadge } from './PremisesBadge';
import { PREMISES_STYLE } from './premisesStyle';
import {
  excelSheet,
  isoDate,
  num as toNum,
  type ExcelWorkbookSpec,
  type ExportFilter,
} from '../util/excel-export';
import { SectionExplainer } from './SectionExplainer';
import { useSectionExport } from '../hooks/useSectionExport';
import { StateProductTimeline } from './StateProductTimeline';
import { PremiumPagination } from './depletions/PremiumPagination';

interface Props {
  range?: DateRange;
}

const num = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const numInt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

// ----------------------------------------------------------------
// Shared visual primitives — mirror DepletionsProductPerformance.
// Kept in-file for now; can be extracted to a depletions shared
// module when a 3rd section needs them.
// ----------------------------------------------------------------

interface RichTooltipProps {
  title: TooltipProps['title'];
  children: ReactElement;
  placement?: TooltipProps['placement'];
  maxWidth?: number;
}

function Tooltip({ title, children, placement = 'top', maxWidth = 320 }: RichTooltipProps) {
  return (
    <MuiTooltip
      title={title}
      placement={placement}
      arrow
      enterDelay={120}
      enterNextDelay={50}
      leaveDelay={40}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.05)',
            fontSize: 11.5,
            lineHeight: 1.45,
            maxWidth,
            padding: '8px 10px',
            fontFeatureSettings: '"tnum","lnum"',
          },
        },
        arrow: {
          sx: {
            color: '#fff',
            '&::before': {
              border: `1px solid ${colors.border}`,
              backgroundColor: '#fff',
            },
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
}

interface TooltipBlockProps {
  title?: string;
  items?: Array<{ label: string; description: string }>;
  bullets?: string[];
  tip?: string;
  maxWidth?: number;
}

function TooltipBlock({ title, items, bullets, tip, maxWidth = 280 }: TooltipBlockProps) {
  return (
    <Box sx={{ p: 0.25, maxWidth }}>
      {title && (
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.textPrimary, mb: 0.75 }}>
          {title}
        </Typography>
      )}
      {items && items.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.6,
            mb: bullets || tip ? 0.75 : 0,
          }}
        >
          {items.map((it) => (
            <Box key={it.label}>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  lineHeight: 1.3,
                }}
              >
                {it.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.45 }}>
                {it.description}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {bullets && bullets.length > 0 && (
        <Box
          component="ul"
          sx={{
            pl: 0,
            m: 0,
            mb: tip ? 0.75 : 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.3,
          }}
        >
          {bullets.map((b, i) => (
            <Box
              component="li"
              key={i}
              sx={{
                fontSize: 11,
                color: colors.textSecondary,
                lineHeight: 1.4,
                position: 'relative',
                pl: 1.25,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 2,
                  top: 7,
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: colors.textMuted,
                },
              }}
            >
              {b}
            </Box>
          ))}
        </Box>
      )}
      {tip && (
        <Typography
          sx={{
            fontSize: 10.5,
            color: colors.textMuted,
            fontStyle: 'italic',
            lineHeight: 1.4,
            borderTop: `1px solid ${colors.borderLight}`,
            pt: 0.6,
            mt: items || bullets ? 0.5 : 0,
          }}
        >
          {tip}
        </Typography>
      )}
    </Box>
  );
}

interface ColumnHeaderProps {
  children: ReactNode;
  tooltip: ReactNode;
  align?: 'left' | 'right';
}

function ColumnHeader({ children, tooltip, align = 'left' }: ColumnHeaderProps) {
  return (
    <Box sx={{ textAlign: align }}>
      <Tooltip title={tooltip}>
        <Box
          component="span"
          sx={{
            borderBottom: `1px dotted ${colors.textMuted}80`,
            cursor: 'help',
            paddingBottom: '1px',
          }}
        >
          {children}
        </Box>
      </Tooltip>
    </Box>
  );
}

const MOMENTUM_PRESENTATION: Record<
  StateMomentum,
  { label: string; color: string; bg: string; Icon: typeof TrendingUpIcon }
> = {
  rising: {
    label: 'Rising',
    color: colors.success,
    bg: `${colors.success}14`,
    Icon: TrendingUpIcon,
  },
  steady: { label: 'Steady', color: colors.textSecondary, bg: '#f1f5f9', Icon: TrendingFlatIcon },
  slipping: {
    label: 'Slipping',
    color: colors.warning,
    bg: `${colors.warning}14`,
    Icon: TrendingDownIcon,
  },
  declining: {
    label: 'Declining',
    color: colors.error,
    bg: `${colors.error}14`,
    Icon: TrendingDownIcon,
  },
  new: {
    label: 'New',
    color: colors.chartPrimary,
    bg: `${colors.chartPrimary}14`,
    Icon: TrendingUpIcon,
  },
};

function shortMonth(iso: string): string {
  const [y, m] = iso.split('-');
  const idx = m ? Number(m) - 1 : 0;
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${names[idx] ?? '?'} '${(y ?? '').slice(-2)}`;
}

// Strip the HY brand prefix so SKU labels fit a row cell.
function shortSkuName(full: string): string {
  for (const prefix of ['Hooten Young ', 'Hooten & Young ', 'Hooten and Young ']) {
    if (full.startsWith(prefix)) return full.slice(prefix.length).trim();
  }
  return full;
}

// ---- per-row bar sparkline ----

interface SparklineProps {
  series: { period: string; cases_9l: string }[];
  color?: string;
}
const BAR_W = 9;
const BAR_GAP = 3;
const BAR_H = 30;
const ZERO_RAIL_H = 2;

function Sparkline({ series, color = colors.chartPrimary }: SparklineProps) {
  const values = series.map((p) => Number(p.cases_9l));
  const max = Math.max(...values, 0);
  const peakIdx = max > 0 ? values.indexOf(max) : -1;
  const lastIdx = series.length - 1;
  const firstLabel = series.length > 0 ? shortMonth(series[0]!.period) : '';
  const lastLabel = series.length > 0 ? shortMonth(series[series.length - 1]!.period) : '';
  const stripW = values.length * BAR_W + (values.length - 1) * BAR_GAP;

  return (
    <Box sx={{ display: 'inline-block' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'flex-end',
          gap: `${BAR_GAP}px`,
          height: BAR_H,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            bgcolor: colors.borderLight,
            zIndex: 0,
          },
        }}
      >
        {values.map((v, i) => {
          const isZero = v === 0;
          const h = max > 0 && v > 0 ? Math.max((v / max) * BAR_H, 4) : ZERO_RAIL_H;
          const isPeak = !isZero && i === peakIdx;
          const isLast = i === lastIdx;
          const period = series[i]?.period ?? '';
          return (
            <Tooltip
              key={period}
              title={
                <Box>
                  <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
                    {shortMonth(period)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: colors.textPrimary,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {isZero ? 'No depletions' : `${fmt6PkNumber(v)} 6-pk`}
                  </Typography>
                  {isPeak && (
                    <Typography sx={{ fontSize: 10, color: colors.success, fontWeight: 700 }}>
                      Peak month
                    </Typography>
                  )}
                  {isLast && !isPeak && (
                    <Typography sx={{ fontSize: 10, color: colors.chartPrimary, fontWeight: 700 }}>
                      Latest month
                    </Typography>
                  )}
                </Box>
              }
            >
              <Box
                sx={{
                  position: 'relative',
                  width: BAR_W,
                  height: h,
                  bgcolor: isZero ? colors.borderLight : color,
                  opacity: isZero ? 1 : isPeak ? 0.95 : 0.45,
                  borderRadius: '2px 2px 0 0',
                  cursor: 'pointer',
                  zIndex: 1,
                  outline: isLast && !isZero ? `1.5px solid ${color}` : 'none',
                  outlineOffset: '-1px',
                  transition: 'opacity 0.15s, transform 0.15s',
                  '&:hover': {
                    opacity: isZero ? 1 : 0.95,
                    transform: isZero ? 'none' : 'scaleY(1.08)',
                    transformOrigin: 'bottom',
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ mt: 0.4, display: 'flex', justifyContent: 'space-between', width: stripW }}>
        <Typography sx={{ fontSize: 9, color: colors.textMuted, lineHeight: 1, fontWeight: 500 }}>
          {firstLabel}
        </Typography>
        <Typography sx={{ fontSize: 9, color: colors.textMuted, lineHeight: 1, fontWeight: 500 }}>
          {lastLabel}
        </Typography>
      </Box>
    </Box>
  );
}

// ---- QoQ / YoY chips with low-base detection ----

const LOW_BASE_THRESHOLD_9L = 5;

function qoqChipText(velocityPct: number | null, prior3m: number, recent3m: number): string {
  if (velocityPct === null) return 'New';
  if (prior3m < LOW_BASE_THRESHOLD_9L) {
    const diff = recent3m - prior3m;
    return `${diff > 0 ? '+' : ''}${fmt6PkNumber(diff)} 6-pk`;
  }
  if (velocityPct > 999) return '+999%+';
  if (velocityPct < -999) return '−999%+';
  return `${velocityPct > 0 ? '+' : ''}${velocityPct.toFixed(0)}%`;
}

interface MomentumChipProps {
  momentum: StateMomentum;
  velocityPct: number | null;
  recent3m: number;
  prior3m: number;
  compact?: boolean;
}

function MomentumChip({
  momentum,
  velocityPct,
  recent3m,
  prior3m,
  compact = false,
}: MomentumChipProps) {
  const p = MOMENTUM_PRESENTATION[momentum];
  const lowBase = velocityPct !== null && prior3m < LOW_BASE_THRESHOLD_9L;
  const text = qoqChipText(velocityPct, prior3m, recent3m);
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.25, maxWidth: 220 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
            Quarter-over-quarter
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
            <span style={{ opacity: 0.7 }}>Recent 3M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(recent3m)} 6-pk
            </span>
            <span style={{ opacity: 0.7 }}>Prior 3M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(prior3m)} 6-pk
            </span>
          </Box>
          {lowBase && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              Tiny prior baseline — showing absolute change instead of a misleading %.
            </Typography>
          )}
          {velocityPct === null && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              No prior-3M activity — state is brand new or restarting.
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
          px: compact ? 0.75 : 1,
          py: compact ? 0.2 : 0.3,
          borderRadius: 1,
          bgcolor: p.bg,
          color: p.color,
          fontWeight: 700,
          fontSize: compact ? 10.5 : 11.5,
          letterSpacing: 0.2,
          fontFeatureSettings: '"tnum"',
          whiteSpace: 'nowrap',
          cursor: 'help',
          border: lowBase ? `1px dashed ${p.color}` : 'none',
        }}
      >
        <p.Icon sx={{ fontSize: compact ? 11 : 13 }} />
        {text}
      </Box>
    </Tooltip>
  );
}

interface YoyChipProps {
  velocityPct: number | null;
  recent12m: number;
  prior12m: number;
}

function YoyChip({ velocityPct, recent12m, prior12m }: YoyChipProps) {
  const LOW_BASE_12M = 20;
  const lowBase = velocityPct !== null && prior12m < LOW_BASE_12M;
  const isNew = velocityPct === null;
  const isPositive = velocityPct !== null && velocityPct > 0;
  const isNegative = velocityPct !== null && velocityPct < 0;
  const color = isNew
    ? colors.textMuted
    : lowBase
      ? colors.textSecondary
      : isPositive
        ? colors.success
        : isNegative
          ? colors.error
          : colors.textSecondary;
  const bg = isNew
    ? 'transparent'
    : lowBase
      ? '#f1f5f9'
      : isPositive
        ? `${colors.success}10`
        : isNegative
          ? `${colors.error}10`
          : '#f1f5f9';
  let text: string;
  if (isNew) {
    text = 'YoY n/a';
  } else if (lowBase) {
    const diff = recent12m - prior12m;
    text = `YoY ${diff > 0 ? '+' : ''}${fmt6PkNumber(diff)} 6-pk`;
  } else {
    const v = velocityPct!;
    const cap = v > 999 ? '+999%+' : v < -999 ? '−999%+' : `${v > 0 ? '+' : ''}${v.toFixed(0)}%`;
    text = `YoY ${cap}`;
  }
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.25, maxWidth: 240 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>Year-over-year</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
            <span style={{ opacity: 0.7 }}>Recent 12M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(recent12m)} 6-pk
            </span>
            <span style={{ opacity: 0.7 }}>Prior 12M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(prior12m)} 6-pk
            </span>
          </Box>
          {lowBase && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              Prior-year baseline was tiny — showing absolute change instead.
            </Typography>
          )}
          {isNew && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              No prior-year activity. Too new for a YoY read.
            </Typography>
          )}
          {!isNew && !lowBase && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              YoY smooths the seasonal noise QoQ catches.
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 0.65,
          py: 0.15,
          borderRadius: 0.75,
          bgcolor: bg,
          color,
          fontWeight: 600,
          fontSize: 10,
          letterSpacing: 0.2,
          fontFeatureSettings: '"tnum"',
          whiteSpace: 'nowrap',
          cursor: 'help',
          border: lowBase ? `1px dashed ${color}` : 'none',
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

// ---- flag chip + tiny inline bar ----

interface FlagChipProps {
  Icon: typeof TrendingUpIcon;
  label: string;
  color: string;
  bg: string;
  tooltip: string;
}

function FlagChip({ Icon, label, color, bg, tooltip }: FlagChipProps) {
  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.3,
          px: 0.55,
          py: 0.15,
          borderRadius: 0.75,
          bgcolor: bg,
          color,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: 0.2,
          textTransform: 'uppercase',
          cursor: 'help',
          whiteSpace: 'nowrap',
        }}
      >
        <Icon sx={{ fontSize: 11 }} />
        {label}
      </Box>
    </Tooltip>
  );
}

interface MiniBarProps {
  share: number;
  color: string;
}

function MiniBar({ share, color }: MiniBarProps) {
  const pct = Math.max(0, Math.min(1, share)) * 100;
  return (
    <Box
      sx={{
        position: 'relative',
        height: 6,
        bgcolor: `${color}1a`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${pct}%`,
          bgcolor: color,
          opacity: 0.85,
          borderRadius: 1,
        }}
      />
    </Box>
  );
}

// ---- portfolio strip tile ----

interface PortfolioTileProps {
  eyebrow: string;
  primary: string;
  primaryColor?: string;
  secondary?: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'error';
  tooltip?: ReactNode;
}

function PortfolioTile({
  eyebrow,
  primary,
  primaryColor,
  secondary,
  tone = 'neutral',
  tooltip,
}: PortfolioTileProps) {
  const toneAccent: Record<NonNullable<PortfolioTileProps['tone']>, string> = {
    neutral: colors.chartPrimary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
  };
  const body = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        py: 1.5,
        borderRight: `1px solid ${colors.borderLight}`,
        '&:last-of-type': { borderRight: 'none' },
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        cursor: tooltip ? 'help' : 'default',
        transition: 'background-color 0.12s',
        '&:hover': tooltip ? { bgcolor: '#fff' } : undefined,
      }}
    >
      <Typography
        sx={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: colors.textMuted,
          textTransform: 'uppercase',
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color: primaryColor ?? toneAccent[tone],
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontFeatureSettings: '"tnum","lnum"',
        }}
      >
        {primary}
      </Typography>
      {secondary !== undefined && (
        <Box sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.3 }}>{secondary}</Box>
      )}
    </Box>
  );
  if (!tooltip) return body;
  return <Tooltip title={tooltip}>{body}</Tooltip>;
}

// ----------------------------------------------------------------
// Sort / filter
// ----------------------------------------------------------------

type SortKey = 'volume' | 'momentum' | 'yoy' | 'accounts' | 'depth';
type FilterKey = 'all' | 'rising' | 'slipping_or_worse';

const SORT_OPTIONS: { key: SortKey; label: string; describe: string }[] = [
  { key: 'volume', label: 'Volume', describe: 'Total 6-pk, largest first' },
  { key: 'momentum', label: 'QoQ momentum', describe: 'Recent vs prior 3M, biggest gain first' },
  { key: 'yoy', label: 'YoY momentum', describe: 'Recent 12M vs prior 12M' },
  { key: 'accounts', label: 'Reach', describe: 'Account count, widest first' },
  { key: 'depth', label: 'Depth', describe: 'Avg 6-pk per account, deepest first' },
];

const FILTER_OPTIONS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All states' },
  { key: 'rising', label: 'Rising only' },
  { key: 'slipping_or_worse', label: 'Slipping / declining' },
];

// ----------------------------------------------------------------
// Yearly comparison — grouped bar chart per state
// ----------------------------------------------------------------

// Three distinct premium hues — each year reads at a glance against
// white. Past = cool teal, present = brand indigo, current YTD = warm
// bourbon amber (HY brand-adjacent). The diagonal-stripe overlay on
// the YTD bar is still applied so it reads as "in progress" without
// thinking. Order: oldest → newest. Replaces the earlier slate scale
// because the lightest tone faded against the white card surface.
const YEAR_COLOR_SCALE = ['#0e7490', '#4f46e5', '#b45309'];

// The YTD bar uses this base color for both the SVG pattern fill and
// the legend swatch. Always the most recent entry in the scale.
const YTD_BASE_COLOR = YEAR_COLOR_SCALE[YEAR_COLOR_SCALE.length - 1]!;

// Months in short form for "Jan-Jun" YTD labels.
const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface YearlyComparisonRow {
  state: string;
  // Dynamic per-year keys, e.g. y2024, y2025, y2026.
  [yearKey: string]: number | string;
  // Sort anchor — total across all years for "by total" ordering.
  totalAcrossYears: number;
  latestYearVolume: number;
}

type YearlySortKey = 'latest_year' | 'total' | 'state_az';

const YEAR_SORT_OPTIONS: { key: YearlySortKey; label: string; describe: string }[] = [
  { key: 'latest_year', label: 'Latest year', describe: 'Current YTD volume, largest first' },
  { key: 'total', label: 'All-time total', describe: '2024+2025+2026 combined' },
  { key: 'state_az', label: 'State A→Z', describe: 'Alphabetical' },
];

interface YearlyComparisonChartProps {
  states: StatePerformanceItem[];
  referenceDate: string | null;
  total9L: number;
}

function YearlyComparisonChart({ states, referenceDate, total9L }: YearlyComparisonChartProps) {
  const [topN, setTopN] = useState<10 | 15 | 24>(15);
  const [sort, setSort] = useState<YearlySortKey>('latest_year');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  // Collect the union of years across all states so we know which
  // bars to render. Backend returns ascending; we trust that order.
  const { years, ytdYear, ytdMonths } = useMemo(() => {
    const yearSet = new Set<number>();
    let ytdYearVal: number | null = null;
    let ytdMonthsVal = 0;
    for (const s of states) {
      for (const y of s.yearly_history) {
        yearSet.add(y.year);
        if (y.is_ytd && (ytdYearVal === null || y.year > ytdYearVal)) {
          ytdYearVal = y.year;
          ytdMonthsVal = y.months_covered;
        }
      }
    }
    return {
      years: [...yearSet].sort((a, b) => a - b),
      ytdYear: ytdYearVal,
      ytdMonths: ytdMonthsVal,
    };
  }, [states]);

  const data: YearlyComparisonRow[] = useMemo(() => {
    return states.map((s) => {
      const byYear = new Map<number, StateYearlyVolume>();
      for (const y of s.yearly_history) byYear.set(y.year, y);
      const row: YearlyComparisonRow = {
        state: s.state_code,
        totalAcrossYears: s.yearly_history.reduce((acc, y) => acc + Number(y.cases_9l), 0),
        latestYearVolume:
          s.yearly_history.length > 0
            ? Number(s.yearly_history[s.yearly_history.length - 1]!.cases_9l)
            : 0,
      };
      for (const yr of years) {
        const entry = byYear.get(yr);
        row[`y${yr}`] = entry ? Number(entry.cases_9l) : 0;
      }
      return row;
    });
  }, [states, years]);

  const sortedData = useMemo(() => {
    const sorted = data.slice();
    switch (sort) {
      case 'state_az':
        sorted.sort((a, b) => a.state.localeCompare(b.state));
        break;
      case 'total':
        sorted.sort((a, b) => b.totalAcrossYears - a.totalAcrossYears);
        break;
      case 'latest_year':
      default:
        sorted.sort((a, b) => b.latestYearVolume - a.latestYearVolume);
    }
    return sorted.slice(0, topN);
  }, [data, sort, topN]);

  // Per-year average across the VISIBLE states only — the lines move
  // with the user's filter (Top 10/15/all). Zero values still count
  // toward the mean since a state with no activity in a given year
  // really is a data point at zero.
  const yearlyAverages = useMemo(() => {
    if (sortedData.length === 0) return new Map<number, number>();
    const map = new Map<number, number>();
    for (const yr of years) {
      const sum = sortedData.reduce(
        (acc, row) => acc + ((row[`y${yr}`] as number | undefined) ?? 0),
        0,
      );
      map.set(yr, sum / sortedData.length);
    }
    return map;
  }, [sortedData, years]);

  const ytdRangeLabel =
    ytdYear && ytdMonths > 0
      ? `${MONTH_SHORT[0]}-${MONTH_SHORT[Math.min(ytdMonths, 12) - 1]} ${ytdYear}`
      : null;
  const referenceLabel = referenceDate
    ? `${MONTH_SHORT[Number(referenceDate.split('-')[1]) - 1] ?? ''} ${referenceDate.slice(0, 4)}`
    : null;
  const sortLabel = YEAR_SORT_OPTIONS.find((o) => o.key === sort)?.label ?? 'Latest year';
  const visibleStates = sortedData.length;
  const totalStates = data.length;

  if (years.length === 0) return null;

  return (
    <Box sx={{ mb: 2.5 }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 1.25,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: colors.textMuted,
              mb: 0.4,
            }}
          >
            Year-over-year by state
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.35 }}>
            {years.join(' vs ')} side-by-side ·{' '}
            {ytdRangeLabel && (
              <Box component="span" sx={{ color: colors.textMuted }}>
                {ytdYear} is YTD ({ytdRangeLabel}) — not a full calendar year
              </Box>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <YearlySortMenu
            label="Sort"
            value={sortLabel}
            open={sortMenuOpen}
            onToggle={() => setSortMenuOpen((o) => !o)}
          >
            {YEAR_SORT_OPTIONS.map((opt) => (
              <YearlyMenuRow
                key={opt.key}
                selected={sort === opt.key}
                primary={opt.label}
                secondary={opt.describe}
                onClick={() => {
                  setSort(opt.key);
                  setSortMenuOpen(false);
                }}
              />
            ))}
          </YearlySortMenu>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
            {([10, 15, 24] as const).map((n) => (
              <Box
                key={n}
                component="button"
                onClick={() => setTopN(n)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  px: 1,
                  py: 0.4,
                  borderRadius: 0.75,
                  fontSize: 11,
                  fontWeight: topN === n ? 700 : 500,
                  color: topN === n ? colors.chartPrimary : colors.textSecondary,
                  bgcolor: topN === n ? `${colors.chartPrimary}10` : 'transparent',
                  border: `1px solid ${topN === n ? colors.chartPrimary : colors.border}`,
                  fontFeatureSettings: '"tnum"',
                  '&:hover': { borderColor: colors.chartPrimary },
                }}
              >
                Top {n === 24 ? 'all' : n}
              </Box>
            ))}
          </Box>
          <Typography
            sx={{
              fontSize: 11,
              color: colors.textMuted,
              fontFeatureSettings: '"tnum"',
              ml: 0.5,
            }}
          >
            {visibleStates} of {totalStates} states
          </Typography>
        </Box>
      </Box>

      {/* Chart */}
      <Box
        sx={{
          border: `1px solid ${colors.borderLight}`,
          borderRadius: 1.5,
          bgcolor: '#fff',
          p: 1.5,
          pb: 0.75,
        }}
      >
        <ResponsiveContainer width="100%" height={Math.max(290, sortedData.length * 18 + 140)}>
          <BarChart
            data={sortedData}
            margin={{ top: 16, right: 130, bottom: 36, left: 18 }}
            barCategoryGap="22%"
            barGap={2}
          >
            <defs>
              <pattern
                id="ytdStripe"
                patternUnits="userSpaceOnUse"
                width={6}
                height={6}
                patternTransform="rotate(45)"
              >
                <rect width={6} height={6} fill={YTD_BASE_COLOR} />
                <line x1={0} y1={0} x2={0} y2={6} stroke="#fff" strokeWidth={1.3} opacity={0.45} />
              </pattern>
            </defs>
            <CartesianGrid stroke={colors.borderLight} vertical={false} strokeDasharray="2 3" />
            <XAxis
              dataKey="state"
              tick={{ fontSize: 11, fill: colors.textPrimary, fontWeight: 700 }}
              axisLine={false}
              tickLine={false}
              interval={0}
            >
              <Label
                value="State"
                position="insideBottom"
                offset={-16}
                style={{
                  fill: colors.textMuted,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              />
            </XAxis>
            <YAxis
              tick={{ fontSize: 10, fill: colors.textMuted }}
              axisLine={false}
              tickLine={false}
              width={62}
              tickFormatter={(v) => numInt.format((v as number) * 2)}
            >
              <Label
                value="6-pk volume"
                angle={-90}
                position="insideLeft"
                offset={6}
                style={{
                  fill: colors.textMuted,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  textAnchor: 'middle',
                }}
              />
            </YAxis>

            {/* Per-year average reference lines — dashed, color-matched
                to the bar palette. Label sits at the far right edge
                with year + average value so the user can immediately
                read "states above this line are above-average for the
                year." Lines update as the user changes the Top N filter. */}
            {years.map((yr, idx) => {
              const avg = yearlyAverages.get(yr) ?? 0;
              if (avg <= 0) return null;
              const lineColor =
                YEAR_COLOR_SCALE[idx] ?? YEAR_COLOR_SCALE[YEAR_COLOR_SCALE.length - 1]!;
              const isYtd = ytdYear === yr;
              return (
                <ReferenceLine
                  key={`avg-${yr}`}
                  y={avg}
                  stroke={lineColor}
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  ifOverflow="extendDomain"
                  label={{
                    // Shorter label so it fits comfortably in the
                    // right margin. The dashed line + color-match
                    // already imply "average" — no need to spell it.
                    value: `${yr}${isYtd ? ' YTD' : ''} · ${fmt6PkNumber(avg)} 6-pk`,
                    position: 'right',
                    offset: 8,
                    fill: lineColor,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.2,
                  }}
                />
              );
            })}
            <RTooltip
              cursor={{ fill: `${colors.chartPrimary}0d` }}
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                const row = (payload[0]?.payload ?? null) as YearlyComparisonRow | null;
                if (!row) return null;
                return (
                  <Box
                    sx={{
                      bgcolor: '#fff',
                      border: `1px solid ${colors.border}`,
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 1,
                      boxShadow: '0 12px 28px rgba(0,0,0,0.10)',
                      minWidth: 200,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.5 }}>{label}</Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto',
                        gap: '4px 8px',
                        alignItems: 'baseline',
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {years.map((yr, idx) => {
                        const val = (row[`y${yr}`] as number | undefined) ?? 0;
                        const isYtd = ytdYear === yr;
                        const color =
                          YEAR_COLOR_SCALE[idx] ?? YEAR_COLOR_SCALE[YEAR_COLOR_SCALE.length - 1]!;
                        return (
                          <Box key={yr} sx={{ display: 'contents' }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: 0.5,
                                bgcolor: color,
                                alignSelf: 'center',
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: 11.5,
                                color: colors.textSecondary,
                                fontWeight: isYtd ? 700 : 500,
                              }}
                            >
                              {yr}
                              {isYtd && (
                                <Box
                                  component="span"
                                  sx={{
                                    color: colors.textMuted,
                                    fontWeight: 500,
                                    ml: 0.5,
                                  }}
                                >
                                  YTD ({ytdRangeLabel})
                                </Box>
                              )}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: colors.textPrimary,
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {fmt6PkNumber(val)} 6-pk
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    {/* YoY commentary — compares the two most recent
                        full years to spot the real trend. */}
                    {years.length >= 2 &&
                      (() => {
                        const fullYears = years.filter((y) => y !== ytdYear);
                        if (fullYears.length < 2) return null;
                        const latestFull = fullYears[fullYears.length - 1]!;
                        const priorFull = fullYears[fullYears.length - 2]!;
                        const a = (row[`y${priorFull}`] as number | undefined) ?? 0;
                        const b = (row[`y${latestFull}`] as number | undefined) ?? 0;
                        if (a === 0) return null;
                        const pct = ((b - a) / a) * 100;
                        const tone =
                          pct > 5 ? colors.success : pct < -5 ? colors.error : colors.textMuted;
                        return (
                          <Typography
                            sx={{
                              mt: 0.75,
                              pt: 0.75,
                              borderTop: `1px solid ${colors.borderLight}`,
                              fontSize: 11,
                              color: tone,
                              fontWeight: 700,
                              fontFeatureSettings: '"tnum"',
                            }}
                          >
                            {priorFull} → {latestFull}: {pct > 0 ? '+' : ''}
                            {pct.toFixed(0)}% YoY
                          </Typography>
                        );
                      })()}
                  </Box>
                );
              }}
            />
            {years.map((yr, idx) => {
              const isYtd = ytdYear === yr;
              const baseColor =
                YEAR_COLOR_SCALE[idx] ?? YEAR_COLOR_SCALE[YEAR_COLOR_SCALE.length - 1]!;
              return (
                <Bar
                  key={yr}
                  dataKey={`y${yr}`}
                  name={isYtd ? `${yr} YTD` : `${yr}`}
                  radius={[3, 3, 0, 0]}
                  // YTD year uses the diagonal-stripe pattern so the
                  // user reads it as "in progress" without a separate
                  // legend.
                  fill={isYtd ? 'url(#ytdStripe)' : baseColor}
                >
                  {sortedData.map((_, j) => (
                    <Cell key={`${yr}-${j}`} />
                  ))}
                </Bar>
              );
            })}
          </BarChart>
        </ResponsiveContainer>

        {/* Custom legend — pairs each year with its swatch + the YTD
            badge so the legend doubles as the explanation. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 2,
            mt: 0.5,
            mb: 0.5,
          }}
        >
          {years.map((yr, idx) => {
            const isYtd = ytdYear === yr;
            const baseColor =
              YEAR_COLOR_SCALE[idx] ?? YEAR_COLOR_SCALE[YEAR_COLOR_SCALE.length - 1]!;
            return (
              <Box key={yr} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, fontSize: 11 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 10,
                    borderRadius: 0.5,
                    background: isYtd
                      ? `repeating-linear-gradient(45deg, ${YTD_BASE_COLOR}, ${YTD_BASE_COLOR} 3px, rgba(255,255,255,0.55) 3px, rgba(255,255,255,0.55) 4.3px)`
                      : baseColor,
                  }}
                />
                <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, fontWeight: 600 }}>
                  {yr}
                </Typography>
                {isYtd && (
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: colors.textMuted,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                    }}
                  >
                    YTD
                  </Typography>
                )}
              </Box>
            );
          })}
          {referenceLabel && (
            <Typography sx={{ fontSize: 10.5, color: colors.textMuted, ml: 1 }}>
              ref {referenceLabel} · portfolio {fmt6PkNumber(total9L)} 6-pk · {num.format(total9L)}{' '}
              9L
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// Local toolbar primitives — small wrappers around the same pattern
// used by the main table toolbar, just dropped here so the YearlyComparisonChart
// is self-contained.
interface YearlySortMenuProps {
  label: string;
  value: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function YearlySortMenu({ label, value, open, onToggle, children }: YearlySortMenuProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, onToggle, open);
  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.4,
          borderRadius: 0.75,
          border: `1px solid ${open ? colors.chartPrimary : colors.border}`,
          bgcolor: open ? `${colors.chartPrimary}08` : '#fff',
          fontSize: 11,
          fontWeight: 500,
          color: colors.textPrimary,
          '&:hover': { borderColor: colors.chartPrimary },
        }}
      >
        <Box component="span" sx={{ color: colors.textMuted, fontSize: 10 }}>
          {label}:
        </Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {value}
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 13, color: colors.textMuted }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 13, color: colors.textMuted }} />
        )}
      </Box>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 10,
            minWidth: 220,
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 28px rgba(0,0,0,0.10)',
            overflow: 'hidden',
            py: 0.5,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

interface YearlyMenuRowProps {
  selected: boolean;
  primary: string;
  secondary?: string;
  onClick: () => void;
}

function YearlyMenuRow({ selected, primary, secondary, onClick }: YearlyMenuRowProps) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        display: 'block',
        width: '100%',
        px: 1.5,
        py: 0.75,
        textAlign: 'left',
        bgcolor: selected ? `${colors.chartPrimary}10` : 'transparent',
        '&:hover': { bgcolor: selected ? `${colors.chartPrimary}18` : colors.surfaceHover },
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: selected ? 700 : 500,
          color: selected ? colors.chartPrimary : colors.textPrimary,
        }}
      >
        {primary}
      </Typography>
      {secondary && (
        <Typography sx={{ fontSize: 10.5, color: colors.textMuted, mt: 0.15 }}>
          {secondary}
        </Typography>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Main component
// ----------------------------------------------------------------

type ViewMode = 'all' | 'control' | 'license';

export function DepletionsGeographicBreakdown({ range }: Props) {
  const { data, isLoading, error } = useStatePerformance(range);
  const [sort, setSort] = useState<SortKey>('volume');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  // View toggle — narrow the chart + table to only Control states,
  // only License states, or both. Default is 'all' so the section
  // boots into its current "everything, control-first" view.
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  // Pagination — 10 rows/page matches Top Accounts. -1 = "All".
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Reset to page 1 when the view toggle changes so reps don't end
  // up on an empty page after narrowing the visible set.
  useEffect(() => {
    setPage(0);
  }, [viewMode]);

  // Apply the view toggle BEFORE any other derivations so the chart,
  // table, summary tiles, and counts all reflect the same slice.
  const allStates = useMemo(() => data?.states ?? [], [data?.states]);
  const states = useMemo(() => {
    if (viewMode === 'control') return allStates.filter((s) => isControlState(s.state_code));
    if (viewMode === 'license') return allStates.filter((s) => !isControlState(s.state_code));
    return allStates;
  }, [allStates, viewMode]);
  const totalAll = useMemo(() => states.reduce((sum, s) => sum + Number(s.cases_9l), 0), [states]);
  // Top-3 share recomputed against the active view's volume so the
  // header number stays internally consistent when filtering.
  const top3Share = useMemo(() => {
    if (states.length === 0 || totalAll === 0) return 0;
    const sorted = [...states].sort((a, b) => Number(b.cases_9l) - Number(a.cases_9l));
    const top3 = sorted.slice(0, 3).reduce((s, x) => s + Number(x.cases_9l), 0);
    return top3 / totalAll;
  }, [states, totalAll]);
  const referenceDate = data?.reference_date ?? null;

  // Counts per view so the toggle buttons can show "Control 17" /
  // "License 7" badges.
  const viewCounts = useMemo(() => {
    let ctrl = 0;
    let lic = 0;
    for (const s of allStates) {
      if (isControlState(s.state_code)) ctrl++;
      else lic++;
    }
    return { all: allStates.length, control: ctrl, license: lic };
  }, [allStates]);

  // Portfolio medians for depth context.
  const portfolioMedians = useMemo(() => {
    if (states.length === 0) return { depth: 0, accounts: 0 };
    const median = (vals: number[]) => {
      const sorted = vals.slice().sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2
        : (sorted[mid] ?? 0);
    };
    return {
      depth: median(states.map((s) => Number(s.avg_9l_per_account))),
      accounts: median(states.map((s) => s.account_count)),
    };
  }, [states]);

  const summary = useMemo(() => {
    if (states.length === 0) return null;
    const sortedByVolume = [...states].sort((a, b) => Number(b.cases_9l) - Number(a.cases_9l));
    const lead = sortedByVolume[0] ?? null;
    const top3 = sortedByVolume.slice(0, 3);
    const withVelocity = states.filter((s) => s.velocity_pct !== null);
    // Top Mover / Biggest Slipper both rank by absolute VOLUME change
    // quarter-over-quarter (recent 3M minus prior 3M), not by percentage —
    // a big state moving real cases beats a tiny state swinging off a small base.
    const withDelta = withVelocity.map((s) => ({
      s,
      delta: Number(s.recent_3m_9l) - Number(s.prior_3m_9l),
    }));
    const topMoverEntry = [...withDelta].sort((a, b) => b.delta - a.delta)[0];
    const topMover = topMoverEntry && topMoverEntry.delta > 0 ? topMoverEntry.s : null;
    const slipperEntry = [...withDelta].sort((a, b) => a.delta - b.delta)[0];
    const biggestSlipper = slipperEntry && slipperEntry.delta < 0 ? slipperEntry.s : null;
    return {
      lead,
      top3,
      topMover,
      topMoverDelta: topMover ? (topMoverEntry?.delta ?? 0) : 0,
      biggestSlipper,
      biggestSlipperDelta: biggestSlipper ? (slipperEntry?.delta ?? 0) : 0,
    };
  }, [states]);

  const visible = useMemo(() => {
    let rows = states.slice();
    if (filter === 'rising') {
      rows = rows.filter((s) => s.momentum === 'rising');
    } else if (filter === 'slipping_or_worse') {
      rows = rows.filter((s) => s.momentum === 'slipping' || s.momentum === 'declining');
    }
    const tieBreak = (a: StatePerformanceItem, b: StatePerformanceItem) =>
      Number(b.cases_9l) - Number(a.cases_9l);
    switch (sort) {
      case 'momentum':
        rows.sort((a, b) => {
          const av = a.velocity_pct ?? -Infinity;
          const bv = b.velocity_pct ?? -Infinity;
          return bv - av || tieBreak(a, b);
        });
        break;
      case 'yoy':
        rows.sort((a, b) => {
          const av = a.yoy_pct ?? -Infinity;
          const bv = b.yoy_pct ?? -Infinity;
          return bv - av || tieBreak(a, b);
        });
        break;
      case 'accounts':
        rows.sort((a, b) => b.account_count - a.account_count || tieBreak(a, b));
        break;
      case 'depth':
        rows.sort(
          (a, b) => Number(b.avg_9l_per_account) - Number(a.avg_9l_per_account) || tieBreak(a, b),
        );
        break;
      case 'volume':
      default:
        rows.sort(tieBreak);
    }
    // Stable secondary group — control states first within whichever
    // user-chosen sort is active. Reps in a control state work the
    // state board (single buyer); license states are distributor work.
    // Grouping puts the two sales motions visually adjacent.
    rows.sort((a, b) => {
      const ac = classifyState(a.state_code) === 'control' ? 0 : 1;
      const bc = classifyState(b.state_code) === 'control' ? 0 : 1;
      return ac - bc;
    });
    return rows;
  }, [states, sort, filter]);

  // Reset to page 1 whenever the filter/sort/rows changes so the
  // user doesn't end up on an empty page after narrowing the list.
  useEffect(() => {
    setPage(0);
  }, [sort, filter, rowsPerPage]);

  const paginated = useMemo(() => {
    if (rowsPerPage === -1) return visible;
    const start = page * rowsPerPage;
    return visible.slice(start, start + rowsPerPage);
  }, [visible, page, rowsPerPage]);

  // Every row of the current view / filter / sort, not just this page.
  useSectionExport(
    () =>
      buildStateExport(visible, [
        // Only a host-supplied range narrows the rows; the default view spans everything.
        range?.from || range?.to
          ? { label: 'Date range', value: `${range.from ?? 'Earliest'} to ${range.to ?? 'latest'}` }
          : { kind: 'info', label: 'Period', value: 'All available data' },
        {
          label: 'State type',
          value:
            viewMode === 'control'
              ? 'Control states'
              : viewMode === 'license'
                ? 'License states'
                : 'All states',
        },
        {
          label: 'Momentum',
          value: FILTER_OPTIONS.find((f) => f.key === filter)?.label ?? 'All states',
        },
        {
          kind: 'sort',
          label: 'Sorted by',
          value: `Control states first, then ${SORT_OPTIONS.find((s) => s.key === sort)?.label ?? 'Volume'} (high → low)`,
        },
        { kind: 'info', label: 'Data through', value: referenceDate ?? '' },
      ]),
    !isLoading && states.length > 0,
  );

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2, border: `1px solid ${colors.error}40`, bgcolor: `${colors.error}05` }}
      >
        <Typography color="error">Failed to load state performance: {error.message}</Typography>
      </Paper>
    );
  }

  if (isLoading || states.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          State Performance
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Typography color="text.secondary">No states to display.</Typography>
          )}
        </Box>
      </Paper>
    );
  }

  const sortLabel = SORT_OPTIONS.find((s) => s.key === sort)?.label ?? 'Volume';
  const filterLabel = FILTER_OPTIONS.find((f) => f.key === filter)?.label ?? 'All states';

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${colors.chartPrimary}14`,
          }}
        >
          <PublicIcon sx={{ color: colors.chartPrimary, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            State Performance
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {states.length} states · {fmt6PkNumber(totalAll)} 6-pk total · Top-3 share{' '}
            {Math.round(top3Share * 100)}%{referenceDate && ` · ref ${shortMonth(referenceDate)}`} ·
            QoQ compares last 3 months to the 3 before · YoY = last 12 vs prior 12
          </Typography>
        </Box>
        <ViewModeToggle mode={viewMode} counts={viewCounts} onChange={setViewMode} />
      </Box>

      {/* Year-over-year by state — three calendar years side by side. */}
      <YearlyComparisonChart states={states} referenceDate={referenceDate} total9L={totalAll} />

      {summary && (
        <>
          {/* Make it explicit that every KPI here is volume-based, not %. */}
          <Typography
            sx={{ fontSize: 11, color: colors.textMuted, display: 'block', mb: 0.75, ml: 0.25 }}
          >
            All four KPIs are{' '}
            <Box component="strong" sx={{ color: colors.textSecondary }}>
              6-pk volume
            </Box>
            -based — Top Market by total volume, Top Mover / Biggest Slipper by volume added or lost
            quarter-over-quarter (not %), and Concentration by the top-3 states&apos; share of
            volume.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 1.5,
              mb: 2.5,
              overflow: 'hidden',
              bgcolor: colors.surfaceSubtle,
            }}
          >
            <PortfolioTile
              eyebrow="Top Market"
              primary={summary.lead ? summary.lead.state_code : '—'}
              secondary={
                summary.lead && (
                  <>
                    <strong style={{ color: colors.textPrimary, fontWeight: 700 }}>
                      {num.format(Number(summary.lead.cases_9l))}
                    </strong>{' '}
                    9L · {Math.round(summary.lead.pct_of_9l * 100)}% of total
                  </>
                )
              }
              tooltip={
                summary.lead ? (
                  <TooltipBlock
                    title="Largest market"
                    items={[
                      {
                        label: summary.lead.state_code,
                        description: `${fmt6PkNumber(summary.lead.cases_9l)} 6-pk · ${Math.round(
                          summary.lead.pct_of_9l * 100,
                        )}% of total portfolio volume`,
                      },
                      {
                        label: 'Reach',
                        description: `${numInt.format(summary.lead.account_count)} accounts across ${summary.lead.product_count} SKUs`,
                      },
                    ]}
                    tip="The market you'd defend first if anything else slipped."
                  />
                ) : (
                  'No states in range.'
                )
              }
            />
            <PortfolioTile
              eyebrow="Top Mover"
              primary={summary.topMover ? summary.topMover.state_code : 'None gaining'}
              tone="success"
              secondary={
                summary.topMover ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MomentumChip
                      momentum={summary.topMover.momentum}
                      velocityPct={summary.topMover.velocity_pct}
                      recent3m={Number(summary.topMover.recent_3m_9l)}
                      prior3m={Number(summary.topMover.prior_3m_9l)}
                      compact
                    />
                    <span>+{fmt6PkNumber(summary.topMoverDelta)} 6-pk QoQ</span>
                  </Box>
                ) : (
                  <span style={{ color: colors.textMuted }}>No state adding volume QoQ</span>
                )
              }
              tooltip={
                summary.topMover ? (
                  <TooltipBlock
                    title="Biggest volume gainer (QoQ)"
                    items={[
                      {
                        label: summary.topMover.state_code,
                        description: `Prior 3M: ${fmt6PkNumber(Number(summary.topMover.prior_3m_9l))} 6-pk → Recent 3M: ${fmt6PkNumber(Number(summary.topMover.recent_3m_9l))} 6-pk  (+${fmt6PkNumber(summary.topMoverDelta)} 6-pk added)`,
                      },
                    ]}
                    tip="Ranked by the actual 6-pk volume added, not percentage. Double down on this state — distribution wins compound."
                  />
                ) : (
                  'No state added volume QoQ over the selected range.'
                )
              }
            />
            <PortfolioTile
              eyebrow="Biggest Slipper"
              primary={summary.biggestSlipper ? summary.biggestSlipper.state_code : 'None slipping'}
              tone="error"
              secondary={
                summary.biggestSlipper ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MomentumChip
                      momentum={summary.biggestSlipper.momentum}
                      velocityPct={summary.biggestSlipper.velocity_pct}
                      recent3m={Number(summary.biggestSlipper.recent_3m_9l)}
                      prior3m={Number(summary.biggestSlipper.prior_3m_9l)}
                      compact
                    />
                    <span>−{fmt6PkNumber(Math.abs(summary.biggestSlipperDelta))} 6-pk QoQ</span>
                  </Box>
                ) : (
                  <span style={{ color: colors.textMuted }}>No state losing volume QoQ</span>
                )
              }
              tooltip={
                summary.biggestSlipper ? (
                  <TooltipBlock
                    title="Biggest volume loss (QoQ)"
                    items={[
                      {
                        label: summary.biggestSlipper.state_code,
                        description: `Prior 3M: ${fmt6PkNumber(Number(summary.biggestSlipper.prior_3m_9l))} 6-pk → Recent 3M: ${fmt6PkNumber(Number(summary.biggestSlipper.recent_3m_9l))} 6-pk  (−${fmt6PkNumber(Math.abs(summary.biggestSlipperDelta))} 6-pk lost)`,
                      },
                    ]}
                    tip="Ranked by the actual 6-pk volume lost, not percentage. Open the row to see which accounts went quiet."
                  />
                ) : (
                  'No state lost volume QoQ over the selected range.'
                )
              }
            />
            <PortfolioTile
              eyebrow="Concentration"
              primary={`${Math.round(top3Share * 100)}%`}
              tone={top3Share > 0.7 ? 'warning' : 'neutral'}
              secondary={
                <>
                  Top-3 states share ·{' '}
                  <strong
                    style={{ color: top3Share > 0.7 ? colors.warning : colors.textSecondary }}
                  >
                    {top3Share > 0.7 ? 'Narrow' : 'Healthy'}
                  </strong>{' '}
                  footprint
                </>
              }
              tooltip={
                <TooltipBlock
                  title="Geographic concentration"
                  items={[
                    {
                      label: `Top-3 share: ${Math.round(top3Share * 100)}%`,
                      description: 'Share of total 6-pk coming from the three biggest states:',
                    },
                    ...summary.top3.map((s, i) => ({
                      label: `${i + 1}. ${s.state_code}`,
                      description: `${fmt6PkNumber(Number(s.cases_9l))} 6-pk · ${Math.round(s.pct_of_9l * 100)}% of total`,
                    })),
                    {
                      label: 'Threshold',
                      description:
                        'Above 70% = narrow footprint. Stable leaders, but a state loss is hard to absorb.',
                    },
                  ]}
                />
              }
            />
          </Box>
        </>
      )}

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <ToolbarMenu
          label="Sort"
          value={sortLabel}
          open={sortMenuOpen}
          onToggle={() => {
            setSortMenuOpen((o) => !o);
            setFilterMenuOpen(false);
          }}
        >
          {SORT_OPTIONS.map((opt) => (
            <MenuRow
              key={opt.key}
              selected={sort === opt.key}
              primary={opt.label}
              secondary={opt.describe}
              onClick={() => {
                setSort(opt.key);
                setSortMenuOpen(false);
              }}
            />
          ))}
        </ToolbarMenu>
        <ToolbarMenu
          label="Filter"
          value={filterLabel}
          open={filterMenuOpen}
          onToggle={() => {
            setFilterMenuOpen((o) => !o);
            setSortMenuOpen(false);
          }}
        >
          {FILTER_OPTIONS.map((opt) => (
            <MenuRow
              key={opt.key}
              selected={filter === opt.key}
              primary={opt.label}
              onClick={() => {
                setFilter(opt.key);
                setFilterMenuOpen(false);
              }}
            />
          ))}
        </ToolbarMenu>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
          {visible.length} of {states.length} states
        </Typography>
      </Box>

      {/* Table */}
      <Box
        sx={{
          border: `1px solid ${colors.borderLight}`,
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '22px 32px 1.4fr 152px 1.3fr 1.2fr 1.4fr 1fr',
            gap: 1,
            px: 1.75,
            py: 1,
            bgcolor: colors.surfaceSubtle,
            borderBottom: `1px solid ${colors.borderLight}`,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          <Box />
          <ColumnHeader
            tooltip={
              <TooltipBlock
                title="Rank"
                bullets={[
                  'Position within the current sort order',
                  'Click any row to open its deep-dive panel',
                ]}
              />
            }
          >
            #
          </ColumnHeader>
          <ColumnHeader
            tooltip={
              <TooltipBlock
                title="State & status flags"
                items={[
                  {
                    label: 'Tier badge',
                    description: 'Rising / Steady / Slipping / Declining / New from QoQ.',
                  },
                ]}
                bullets={[
                  'Hot — recent 3M growing > 25%',
                  'Concentrated — one account ≥ 40%',
                  'Single-SKU — one product ≥ 70% of state',
                  'Dist risk — one distributor ≥ 50%',
                  'Dark — > 60 days since last activity',
                  'Bleeding — net account loss ≥ 5 in 90d',
                ]}
              />
            }
          >
            State
          </ColumnHeader>
          <ColumnHeader
            tooltip={
              <TooltipBlock
                title="12-month volume trend"
                bullets={[
                  'Bars run oldest → latest, left to right',
                  'Tallest bar = peak month',
                  'Outlined bar = current month',
                  'Rail bar = no depletions that month',
                ]}
                tip="Hover any bar for the exact month + 6-pk volume."
              />
            }
          >
            12-mo Trend
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Volume & share"
                items={[
                  { label: 'Volume', description: 'Total 6-pk over the selected date range.' },
                  {
                    label: 'Share',
                    description: "This state's portion of total portfolio volume.",
                  },
                ]}
                tip="Number is tinted by momentum: green if rising, red if declining."
              />
            }
          >
            Volume · Share
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Distribution shape"
                items={[
                  {
                    label: 'Reach',
                    description: 'Active accounts in this state and the SKUs they buy.',
                  },
                  {
                    label: 'Depth',
                    description: 'Avg 6-pk per account — compared to the portfolio median.',
                  },
                  {
                    label: 'Net 90d',
                    description: 'Accounts gained vs churned in the last 3 months.',
                  },
                ]}
              />
            }
          >
            Reach · Depth
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Top SKU + top account"
                items={[
                  {
                    label: 'Top SKU',
                    description:
                      'The product that drives the most 6-pk in this state, with its share.',
                  },
                  {
                    label: 'Top account',
                    description: 'Largest single-account share — amber flag when ≥ 40%.',
                  },
                ]}
                tip="Hover for the full top-3 SKU and account lists."
              />
            }
          >
            Top SKU · Account
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Momentum"
                items={[
                  {
                    label: 'QoQ',
                    description:
                      'Recent 3M vs prior 3M. Falls back to absolute Δ on tiny baselines.',
                  },
                  {
                    label: 'YoY',
                    description: 'Recent 12M vs prior 12M — smooths seasonal noise.',
                  },
                ]}
                tip="When QoQ is red but YoY is flat, the dip is likely seasonal."
              />
            }
          >
            QoQ · YoY
          </ColumnHeader>
        </Box>

        {paginated.map((s, i) => {
          // Rank continues across pages — page 2 starts at 11 with
          // 10 rows/page. "All" mode uses the index directly.
          const rank = rowsPerPage === -1 ? i + 1 : page * rowsPerPage + i + 1;
          // Section header when the classification boundary appears.
          const cls = classifyState(s.state_code);
          const prevCls = i === 0 ? null : classifyState(paginated[i - 1]?.state_code);
          const showHeader = i === 0 || cls !== prevCls;
          return (
            <Box key={s.state_code}>
              {showHeader && <GeoSectionHeader classification={cls} states={paginated} />}
              <StateRow index={rank} item={s} portfolioMedians={portfolioMedians} />
            </Box>
          );
        })}

        {visible.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', color: colors.textMuted, fontSize: 12 }}>
            No states match the current filter.
          </Box>
        )}
      </Box>

      <PremiumPagination
        count={visible.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        itemLabel="states"
      />

      {/* Reader's guide — explains the state-row vocabulary (control
          state, premises mix, momentum chip), the group divider, and
          the YoY chart at the top. Collapsed by default. */}
      <SectionExplainer
        rows={[
          {
            sample: (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  px: 0.6,
                  py: 0.15,
                  borderRadius: 0.75,
                  color: '#7c3aed',
                  bgcolor: '#7c3aed14',
                  border: `1px solid #7c3aed40`,
                  lineHeight: 1.4,
                }}
              >
                CTRL
              </Box>
            ),
            term: 'CTRL chip next to a state code',
            desc: (
              <>
                Marks one of the 17 US <strong>alcohol control states</strong>. The sales motion in
                these states runs through the <strong>state liquor board</strong> — not independent
                distributors — so listings and promo approvals work differently than the license
                states.
              </>
            ),
          },
          {
            sample: <GeoControlGroupSwatch />,
            term: 'Control / License group dividers',
            desc: (
              <>
                The state list is stably sorted so <strong>control states cluster first</strong>,
                then license states. A subtle divider with the group's account + 6-pk subtotal
                appears at the boundary.
              </>
            ),
          },
          {
            sample: <GeoPremisesMiniBar />,
            term: 'Premises mini-bar in each state row',
            desc: (
              <>
                The thin stacked bar next to the state header shows the{' '}
                <strong>account-count split</strong> for that state across <strong>ON</strong>{' '}
                (green), <strong>OFF</strong> (blue), <strong>NA</strong> (amber), and{' '}
                <strong>Unknown</strong> (grey). Hover for the raw counts.
              </>
            ),
          },
          {
            sample: <GeoMomentumChip />,
            term: 'State momentum chip (Hot / Growing / Steady / Slipping)',
            desc: (
              <>
                Compares the state's <strong>recent 3M</strong> 6-pk vs the{' '}
                <strong>3M before</strong>. Lets you sort/filter to find states accelerating or
                cooling. Low-base markers fire when the prior period was tiny so % changes don't
                mislead.
              </>
            ),
          },
          {
            sample: (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.3,
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  textTransform: 'uppercase',
                  color: colors.warning,
                  bgcolor: `${colors.warning}14`,
                  border: `1px solid ${colors.warning}40`,
                  px: 0.5,
                  py: 0.15,
                  borderRadius: 0.75,
                  whiteSpace: 'nowrap',
                }}
              >
                FLAG
              </Box>
            ),
            term: 'Flag chips next to the state header',
            desc: (
              <>
                Quick health flags derived from the row data: <strong>Concentrated</strong> (one
                account &gt;~30% of state), <strong>Dist risk</strong> (single distributor
                dominates), <strong>Bleeding</strong> (steep recent drop). Hover for the exact
                criteria.
              </>
            ),
          },
          {
            sample: <GeoYearChartSwatch />,
            term: 'YoY grouped-bar chart at the top',
            desc: (
              <>
                Shows the top-N states' total 6-pk volume by calendar year. Toggle top 10 / 15 / all
                and the sort key (latest year, total, A–Z). The dashed reference line per series is
                the <strong>average across the visible states</strong> for that year.
              </>
            ),
          },
          {
            sample: (
              <Box
                component="span"
                sx={{ fontSize: 14, color: colors.chartPrimary, fontWeight: 700, lineHeight: 1 }}
              >
                ▸
              </Box>
            ),
            term: 'Click a row to expand',
            desc: (
              <>
                Opens a deep-dive panel: a{' '}
                <strong>year-over-year mini chart for every product</strong> in the state (Jan–Dec,
                one line per year, with product/year filters), plus the top SKUs, accounts,
                distributors, and the QoQ + YoY momentum windows.
              </>
            ),
          },
        ]}
      />
    </Paper>
  );
}

// ----------------------------------------------------------------
// Geo SectionExplainer sample swatches
// ----------------------------------------------------------------

function GeoControlGroupSwatch() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.25,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.4,
          fontSize: 8,
          fontWeight: 700,
          color: '#7c3aed',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            bgcolor: '#7c3aed',
            borderRadius: '50%',
          }}
        />
        Control
      </Box>
      <Box sx={{ width: 36, height: 1, bgcolor: '#7c3aed', opacity: 0.4 }} />
      <Box
        sx={{
          fontSize: 8,
          fontWeight: 700,
          color: colors.textMuted,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        License
      </Box>
    </Box>
  );
}

function GeoPremisesMiniBar() {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        height: 5,
        width: 56,
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${colors.borderLight}`,
      }}
    >
      <Box sx={{ width: '24%', bgcolor: colors.success, opacity: 0.85 }} />
      <Box sx={{ width: '64%', bgcolor: colors.info, opacity: 0.85 }} />
      <Box sx={{ width: '10%', bgcolor: colors.warning, opacity: 0.85 }} />
      <Box sx={{ width: '2%', bgcolor: colors.textMuted, opacity: 0.85 }} />
    </Box>
  );
}

function GeoMomentumChip() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: colors.success,
        bgcolor: `${colors.success}14`,
        border: `1px solid ${colors.success}40`,
        px: 0.6,
        py: 0.2,
        borderRadius: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      Hot
    </Box>
  );
}

function GeoYearChartSwatch() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 20 }}>
      {[10, 14, 12, 18, 16].map((h, i) => (
        <Box
          key={i}
          sx={{
            width: 4,
            height: h,
            bgcolor: i % 2 === 0 ? colors.chartPrimary : colors.success,
            borderRadius: '1px 1px 0 0',
            opacity: 0.7 + (i / 5) * 0.3,
          }}
        />
      ))}
    </Box>
  );
}

// ----------------------------------------------------------------
// Row + deep-dive panel
// ----------------------------------------------------------------

interface StateRowProps {
  index: number;
  item: StatePerformanceItem;
  portfolioMedians: { depth: number; accounts: number };
}

// velocity_pct / yoy_pct arrive as 0..100; shares as 0..1.
const pctFraction = (v: number | null): number | null => (v === null ? null : v / 100);

function buildStateExport(
  rows: StatePerformanceItem[],
  filters: ExportFilter[],
): ExcelWorkbookSpec {
  const years = Array.from(new Set(rows.flatMap((s) => s.yearly_history.map((y) => y.year)))).sort(
    (a, b) => a - b,
  );
  const ytdYears = new Set(
    rows.flatMap((s) => s.yearly_history.filter((y) => y.is_ytd).map((y) => y.year)),
  );
  const byYear = rows.map((s) => ({
    state: s.state_code,
    vol: new Map(s.yearly_history.map((y) => [y.year, toNum(y.cases_9l)])),
  }));
  return {
    title: 'State Breakdown',
    filters,
    sheets: [
      excelSheet({
        name: 'States',
        rows,
        columns: [
          { header: 'State', value: (s) => s.state_code, width: 7 },
          { header: 'Type', value: (s) => classifyState(s.state_code), width: 9 },
          { header: '9L Cases', value: (s) => toNum(s.cases_9l), format: 'decimal' },
          { header: 'Physical Cases', value: (s) => toNum(s.cases_physical), format: 'decimal' },
          { header: 'Share of 9L', value: (s) => s.pct_of_9l, format: 'percent' },
          { header: 'Accounts', value: (s) => s.account_count, format: 'int' },
          { header: 'Products', value: (s) => s.product_count, format: 'int' },
          {
            header: 'Avg 9L / Account',
            value: (s) => toNum(s.avg_9l_per_account),
            format: 'decimal',
          },
          { header: 'Top Product', value: (s) => s.top_product_name, width: 30 },
          { header: 'Top Product Share', value: (s) => s.top_product_share, format: 'percent' },
          { header: 'Top Account', value: (s) => s.top_account_name, width: 30 },
          { header: 'Top Account Share', value: (s) => s.top_account_share, format: 'percent' },
          { header: 'Top Distributor', value: (s) => s.top_distributor_code },
          {
            header: 'Top Distributor Share',
            value: (s) => s.top_distributor_share,
            format: 'percent',
          },
          { header: 'Distributors', value: (s) => s.distributor_count, format: 'int' },
          { header: 'Recent 3M 9L', value: (s) => toNum(s.recent_3m_9l), format: 'decimal' },
          { header: 'Prior 3M 9L', value: (s) => toNum(s.prior_3m_9l), format: 'decimal' },
          { header: 'QoQ Velocity', value: (s) => pctFraction(s.velocity_pct), format: 'percent' },
          { header: 'Momentum', value: (s) => s.momentum, width: 11 },
          { header: 'Recent 12M 9L', value: (s) => toNum(s.recent_12m_9l), format: 'decimal' },
          { header: 'Prior 12M 9L', value: (s) => toNum(s.prior_12m_9l), format: 'decimal' },
          { header: 'YoY', value: (s) => pctFraction(s.yoy_pct), format: 'percent' },
          { header: 'Accounts Gained 90d', value: (s) => s.accounts_gained_90d, format: 'int' },
          { header: 'Accounts Churned 90d', value: (s) => s.accounts_churned_90d, format: 'int' },
          { header: 'On-premise Accts', value: (s) => s.premises_counts.on, format: 'int' },
          { header: 'Off-premise Accts', value: (s) => s.premises_counts.off, format: 'int' },
          { header: 'N/A Premise Accts', value: (s) => s.premises_counts.na, format: 'int' },
          {
            header: 'Unknown Premise Accts',
            value: (s) => s.premises_counts.unknown,
            format: 'int',
          },
          { header: 'First Active', value: (s) => isoDate(s.first_active), format: 'date' },
          { header: 'Last Active', value: (s) => isoDate(s.last_active), format: 'date' },
          { header: 'Peak Month', value: (s) => isoDate(s.peak_month), format: 'date' },
          { header: 'Peak Month 9L', value: (s) => toNum(s.peak_month_9l), format: 'decimal' },
        ],
      }),
      ...(years.length > 0
        ? [
            excelSheet({
              name: 'By Year',
              rows: byYear,
              columns: [
                { header: 'State', value: (r) => r.state, width: 7 },
                ...years.map((y) => ({
                  header: `${y}${ytdYears.has(y) ? ' YTD' : ''} 9L`,
                  value: (r: (typeof byYear)[number]) => r.vol.get(y) ?? null,
                  format: 'decimal' as const,
                })),
              ],
            }),
          ]
        : []),
    ],
  };
}

const FLAG_CONCENTRATED_THRESHOLD = 0.4;
const FLAG_SINGLE_SKU_THRESHOLD = 0.7;
const FLAG_DIST_RISK_THRESHOLD = 0.5;
const FLAG_DARK_DAYS = 60;
const FLAG_NET_LOSS_THRESHOLD = 5;

function daysSinceIso(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(`${iso}T00:00:00Z`).getTime();
  const now = Date.now();
  return Math.floor((now - then) / (1000 * 60 * 60 * 24));
}

function StateRow({ index, item, portfolioMedians }: StateRowProps) {
  const presentation = MOMENTUM_PRESENTATION[item.momentum];
  const [expanded, setExpanded] = useState(false);

  const volume = Number(item.cases_9l);
  const physical = Number(item.cases_physical);
  const avgPerAccount = Number(item.avg_9l_per_account);
  const sharePct = item.pct_of_9l * 100;
  const topProdSharePct = item.top_product_share * 100;
  const topAcctSharePct = item.top_account_share * 100;
  const topAcctRisky = topAcctSharePct >= 40;
  const recent3m = Number(item.recent_3m_9l);
  const prior3m = Number(item.prior_3m_9l);
  const daysSinceLast = daysSinceIso(item.last_active);

  // Flags
  const flags: Array<{
    key: string;
    Icon: typeof TrendingUpIcon;
    label: string;
    color: string;
    bg: string;
    tooltip: string;
  }> = [];
  if (item.momentum === 'rising') {
    flags.push({
      key: 'hot',
      Icon: LocalFireDepartmentIcon,
      label: 'Hot',
      color: colors.success,
      bg: `${colors.success}14`,
      tooltip: `Rising — QoQ ${qoqChipText(item.velocity_pct, prior3m, recent3m)} vs prior 3 months.`,
    });
  }
  if (item.top_account_share >= FLAG_CONCENTRATED_THRESHOLD && item.top_account_name) {
    flags.push({
      key: 'conc',
      Icon: WarningAmberIcon,
      label: 'Concentrated',
      color: colors.warning,
      bg: `${colors.warning}14`,
      tooltip: `${item.top_account_name} carries ${topAcctSharePct.toFixed(0)}% of ${item.state_code}. Losing them would hurt the market disproportionately.`,
    });
  }
  if (item.top_product_share >= FLAG_SINGLE_SKU_THRESHOLD && item.top_product_name) {
    flags.push({
      key: 'singlesku',
      Icon: WineBarIcon,
      label: 'Single-SKU',
      color: colors.info,
      bg: `${colors.info}14`,
      tooltip: `${shortSkuName(item.top_product_name)} = ${topProdSharePct.toFixed(0)}% of ${item.state_code}. Cross-sell more SKUs here.`,
    });
  }
  if (item.top_distributor_share >= FLAG_DIST_RISK_THRESHOLD && item.top_distributor_code) {
    flags.push({
      key: 'dist',
      Icon: WarehouseIcon,
      label: 'Dist risk',
      color: colors.warning,
      bg: `${colors.warning}14`,
      tooltip: `${item.top_distributor_code} carries ${Math.round(item.top_distributor_share * 100)}% of ${item.state_code} across ${item.distributor_count} distributors.`,
    });
  }
  if (daysSinceLast !== null && daysSinceLast > FLAG_DARK_DAYS) {
    flags.push({
      key: 'dark',
      Icon: NightsStayIcon,
      label: 'Dark',
      color: colors.error,
      bg: `${colors.error}14`,
      tooltip: `No depletion activity for ${daysSinceLast} days. Last active ${
        item.last_active ? shortMonth(item.last_active) : '—'
      }.`,
    });
  }
  const net90d = item.accounts_gained_90d - item.accounts_churned_90d;
  if (net90d <= -FLAG_NET_LOSS_THRESHOLD) {
    flags.push({
      key: 'bleeding',
      Icon: TrendingDownIcon,
      label: 'Bleeding',
      color: colors.error,
      bg: `${colors.error}14`,
      tooltip: `Net account loss of ${Math.abs(net90d)} in the last 3 months (${item.accounts_gained_90d} gained, ${item.accounts_churned_90d} churned).`,
    });
  }

  // Depth signal
  const depthPctVsMedian =
    portfolioMedians.depth > 0
      ? ((avgPerAccount - portfolioMedians.depth) / portfolioMedians.depth) * 100
      : 0;
  const depthSignal: { label: string; color: string } = (() => {
    if (avgPerAccount === 0) return { label: 'no depth', color: colors.textMuted };
    if (depthPctVsMedian >= 25) return { label: 'deeper than median', color: colors.success };
    if (depthPctVsMedian <= -25) return { label: 'thinner than median', color: colors.warning };
    return { label: 'around median', color: colors.textMuted };
  })();

  const volumeColor =
    item.momentum === 'rising'
      ? colors.success
      : item.momentum === 'declining'
        ? colors.error
        : colors.textPrimary;
  const rowTint =
    item.momentum === 'rising'
      ? `${colors.success}06`
      : item.momentum === 'declining'
        ? `${colors.error}06`
        : 'transparent';

  return (
    <Box
      sx={{
        bgcolor: expanded ? colors.surfaceSubtle : rowTint,
        borderBottom: `1px solid ${colors.borderLight}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((x) => !x);
          }
        }}
        sx={{
          display: 'grid',
          gridTemplateColumns: '22px 32px 1.4fr 152px 1.3fr 1.2fr 1.4fr 1fr',
          gap: 1,
          px: 1.75,
          py: 1.25,
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'background-color 0.12s',
          '&:hover': { bgcolor: colors.surfaceHover },
          '&:focus-visible': {
            outline: `2px solid ${colors.chartPrimary}`,
            outlineOffset: -2,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ChevronRightIcon
            sx={{
              fontSize: 16,
              color: colors.textMuted,
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.18s ease',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 3,
              alignSelf: 'stretch',
              bgcolor: presentation.color,
              borderRadius: 1,
              opacity: 0.7,
              mr: -0.25,
            }}
          />
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color: colors.textMuted,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {index}
          </Typography>
        </Box>

        {/* State code + momentum badge + flags */}
        <Box sx={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 800,
                color: colors.textPrimary,
                letterSpacing: '-0.01em',
              }}
            >
              {item.state_code}
            </Typography>
            <ControlStateChip stateCode={item.state_code} compact />
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.textMuted,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {item.product_count} SKUs · {item.distributor_count} dist
            </Typography>
            <PremisesMiniBar counts={item.premises_counts} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4, flexWrap: 'wrap' }}>
            <Box
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                color: presentation.color,
                px: 0.6,
                py: 0.1,
                borderRadius: 0.75,
                bgcolor: presentation.bg,
              }}
            >
              {presentation.label}
            </Box>
            {flags.map((f) => (
              <FlagChip
                key={f.key}
                Icon={f.Icon}
                label={f.label}
                color={f.color}
                bg={f.bg}
                tooltip={f.tooltip}
              />
            ))}
          </Box>
        </Box>

        {/* Sparkline */}
        <Tooltip
          title={
            <Box sx={{ p: 0.5, fontSize: 11 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
                12-month volume
              </Typography>
              {item.monthly_series.map((s) => (
                <Box
                  key={s.period}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  <span style={{ opacity: 0.7 }}>{shortMonth(s.period)}</span>
                  <span style={{ fontWeight: 600 }}>{fmt6PkNumber(s.cases_9l)} 6-pk</span>
                </Box>
              ))}
            </Box>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
            <Sparkline series={item.monthly_series} color={presentation.color} />
          </Box>
        </Tooltip>

        {/* Volume + share */}
        <Tooltip
          title={
            <Box sx={{ p: 0.5, fontSize: 11 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
                Volume breakdown
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
                <span style={{ opacity: 0.7 }}>6-pack equivalents</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {fmt6PkNumber(volume)}
                </span>
                <span style={{ opacity: 0.7 }}>9L equivalents</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {num.format(volume)}
                </span>
                <span style={{ opacity: 0.7 }}>Physical cases</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {physical > 0 ? num.format(physical) : '—'}
                </span>
                <span style={{ opacity: 0.7 }}>Share of portfolio</span>
                <span style={{ fontWeight: 600 }}>{sharePct.toFixed(1)}%</span>
              </Box>
            </Box>
          }
        >
          <Box sx={{ textAlign: 'right', cursor: 'help' }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: volumeColor,
                lineHeight: 1,
                fontFeatureSettings: '"tnum","lnum"',
              }}
            >
              {fmt6PkNumber(volume)}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: colors.textMuted, mt: 0.25 }}>
              6-pk · {num.format(volume)} 9L · {sharePct.toFixed(1)}% share
            </Typography>
          </Box>
        </Tooltip>

        {/* Reach + depth + net 90d */}
        <Tooltip
          title={
            <Box sx={{ p: 0.5, fontSize: 11 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
                Reach &amp; depth
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
                <span style={{ opacity: 0.7 }}>Active accounts (total)</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {numInt.format(item.account_count)}
                </span>
                <span style={{ opacity: 0.7 }}>Active in last 3M</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {numInt.format(item.accounts_active_90d)}
                </span>
                <span style={{ opacity: 0.7 }}>Gained in last 3M</span>
                <span
                  style={{
                    fontWeight: 600,
                    fontFeatureSettings: '"tnum"',
                    color: colors.success,
                  }}
                >
                  +{numInt.format(item.accounts_gained_90d)}
                </span>
                <span style={{ opacity: 0.7 }}>Churned in last 3M</span>
                <span
                  style={{
                    fontWeight: 600,
                    fontFeatureSettings: '"tnum"',
                    color: colors.error,
                  }}
                >
                  −{numInt.format(item.accounts_churned_90d)}
                </span>
                <span style={{ opacity: 0.7 }}>SKUs sold</span>
                <span style={{ fontWeight: 600 }}>{item.product_count}</span>
                <span style={{ opacity: 0.7 }}>Avg 6-pk per acct</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {num.format(avgPerAccount)}
                </span>
                <span style={{ opacity: 0.7 }}>Portfolio median</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {fmt6PkNumber(portfolioMedians.depth)} 6-pk/acct
                </span>
              </Box>
            </Box>
          }
        >
          <Box sx={{ textAlign: 'right', cursor: 'help' }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.textPrimary,
                lineHeight: 1,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {numInt.format(item.account_count)}
              <Box
                component="span"
                sx={{ color: colors.textMuted, fontWeight: 400, ml: 0.5, fontSize: 11 }}
              >
                accts · {item.product_count} skus
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: 10.5,
                color: depthSignal.color,
                mt: 0.25,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {fmt6PkNumber(avgPerAccount)} 6-pk/acct · {depthSignal.label}
            </Typography>
            <NetAccountsLine
              gained={item.accounts_gained_90d}
              churned={item.accounts_churned_90d}
            />
          </Box>
        </Tooltip>

        {/* Top SKU + Top account */}
        <Tooltip
          title={
            <Box sx={{ p: 0.5, fontSize: 11, minWidth: 220 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.75 }}>
                Top SKUs in {item.state_code}
              </Typography>
              {item.top_products.length === 0 ? (
                <Typography sx={{ fontSize: 11, opacity: 0.7 }}>No product data</Typography>
              ) : (
                item.top_products.map((p) => (
                  <Box
                    key={p.product_id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 0.75,
                      alignItems: 'baseline',
                      mb: 0.4,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{shortSkuName(p.product_name)}</span>
                    <span style={{ fontWeight: 700, color: colors.textSecondary }}>
                      {(p.share * 100).toFixed(0)}%
                    </span>
                  </Box>
                ))
              )}
              {item.top_account_name && (
                <>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, mt: 0.75, mb: 0.4 }}>
                    Top account
                  </Typography>
                  <Typography sx={{ fontSize: 11 }}>
                    {item.top_account_name} · {topAcctSharePct.toFixed(0)}%
                  </Typography>
                </>
              )}
            </Box>
          }
        >
          <Box sx={{ textAlign: 'right', cursor: 'help' }}>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textPrimary,
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item.top_product_name ? shortSkuName(item.top_product_name) : '—'}
              <Box
                component="span"
                sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5, fontSize: 11 }}
              >
                {topProdSharePct.toFixed(0)}%
              </Box>
            </Typography>
            {item.top_account_name && (
              <Typography
                sx={{
                  fontSize: 10.5,
                  color: topAcctRisky ? colors.warning : colors.textMuted,
                  mt: 0.25,
                  fontFeatureSettings: '"tnum"',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {topAcctRisky ? '⚠ ' : ''}Top acct {topAcctSharePct.toFixed(0)}%
              </Typography>
            )}
          </Box>
        </Tooltip>

        {/* QoQ + YoY chips */}
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.4 }}
          onClick={(e) => e.stopPropagation()}
        >
          <MomentumChip
            momentum={item.momentum}
            velocityPct={item.velocity_pct}
            recent3m={recent3m}
            prior3m={prior3m}
          />
          <YoyChip
            velocityPct={item.yoy_pct}
            recent12m={Number(item.recent_12m_9l)}
            prior12m={Number(item.prior_12m_9l)}
          />
        </Box>
      </Box>

      {expanded && <ExpandedDeepDive item={item} portfolioMedians={portfolioMedians} />}
    </Box>
  );
}

interface NetAccountsLineProps {
  gained: number;
  churned: number;
}

function NetAccountsLine({ gained, churned }: NetAccountsLineProps) {
  if (gained === 0 && churned === 0) {
    return (
      <Typography sx={{ fontSize: 10, color: colors.textMuted, mt: 0.2 }}>
        No acct change (90d)
      </Typography>
    );
  }
  const net = gained - churned;
  const netColor = net > 0 ? colors.success : net < 0 ? colors.error : colors.textMuted;
  return (
    <Typography
      sx={{
        fontSize: 10,
        color: colors.textMuted,
        mt: 0.2,
        fontFeatureSettings: '"tnum"',
      }}
    >
      <Box component="span" sx={{ color: colors.success, fontWeight: 600 }}>
        +{gained}
      </Box>
      {' / '}
      <Box component="span" sx={{ color: colors.error, fontWeight: 600 }}>
        −{churned}
      </Box>{' '}
      <Box component="span" sx={{ color: netColor, fontWeight: 700 }}>
        ({net > 0 ? '+' : ''}
        {net} net)
      </Box>{' '}
      90d
    </Typography>
  );
}

// ----------------------------------------------------------------
// Expand panel
// ----------------------------------------------------------------

interface ExpandedDeepDiveProps {
  item: StatePerformanceItem;
  portfolioMedians: { depth: number; accounts: number };
}

function ExpandedDeepDive({ item, portfolioMedians }: ExpandedDeepDiveProps) {
  const presentation = MOMENTUM_PRESENTATION[item.momentum];
  const recent3m = Number(item.recent_3m_9l);
  const prior3m = Number(item.prior_3m_9l);
  const maxBaselineWidth = Math.max(recent3m, prior3m, 1);
  const physical = Number(item.cases_physical);
  const volume = Number(item.cases_9l);
  const avgPerAccount = Number(item.avg_9l_per_account);
  const daysSinceLast = daysSinceIso(item.last_active);
  const recent12m = Number(item.recent_12m_9l);
  const prior12m = Number(item.prior_12m_9l);
  const peakMonthLabel = item.peak_month ? shortMonth(item.peak_month) : null;
  const peakMonthName = item.peak_month
    ? (['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
        Number(item.peak_month.split('-')[1]) - 1
      ] ?? null)
    : null;
  const net90d = item.accounts_gained_90d - item.accounts_churned_90d;
  const rhythmLabel =
    item.months_active_12m === 12
      ? 'Active every month'
      : item.months_active_12m >= 9
        ? 'Mostly active'
        : item.months_active_12m >= 5
          ? 'Sporadic'
          : 'Rare';

  const sectionTitle = (text: string) => (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: colors.textMuted,
        mb: 1,
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderTop: `1px dashed ${colors.borderLight}`,
        bgcolor: colors.surfaceSubtle,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1.4fr' },
        gap: 2.5,
      }}
    >
      {/* Full-width stacked product breakdown for this state — every
          product as a band, stacked to the state's monthly total across
          its full history. */}
      <Box sx={{ gridColumn: '1 / -1' }}>
        <StateProductTimeline stateCode={item.state_code} />
      </Box>

      {/* Top SKUs */}
      <Box>
        {sectionTitle('Top SKUs')}
        {item.top_products.length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>No SKU data.</Typography>
        ) : (
          item.top_products.map((p) => (
            <Box key={p.product_id} sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 0.3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.textPrimary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={p.product_name}
                >
                  {shortSkuName(p.product_name)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum"',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmt6PkNumber(Number(p.cases_9l))} 6-pk
                  <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                    {(p.share * 100).toFixed(1)}%
                  </Box>
                </Typography>
              </Box>
              <MiniBar share={p.share} color={presentation.color} />
            </Box>
          ))
        )}
      </Box>

      {/* Top accounts */}
      <Box>
        {sectionTitle('Top accounts')}
        {item.top_accounts.length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>No account data.</Typography>
        ) : (
          item.top_accounts.map((a) => (
            <Box key={a.account_id} sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 0.3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.textPrimary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={a.name}
                  >
                    {a.name}
                  </Typography>
                  <PremisesBadge premises={a.premises_type} online={a.is_online_retailer} />
                  {a.city && (
                    <Box
                      component="span"
                      sx={{ fontSize: 10.5, color: colors.textMuted, fontWeight: 500 }}
                    >
                      {a.city}
                    </Box>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum"',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmt6PkNumber(a.cases_9l)} 6-pk · {num.format(Number(a.cases_9l))} 9L
                  <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                    {(a.share * 100).toFixed(1)}%
                  </Box>
                </Typography>
              </Box>
              <MiniBar share={a.share} color={presentation.color} />
            </Box>
          ))
        )}
      </Box>

      {/* Top distributors */}
      <Box>
        <Box
          sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}
        >
          {sectionTitle('Top distributors')}
          {item.distributor_count > 0 && (
            <Typography sx={{ fontSize: 10, color: colors.textMuted, fontWeight: 500 }}>
              {item.distributor_count} total
            </Typography>
          )}
        </Box>
        {item.top_distributors.length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
            No distributor data.
          </Typography>
        ) : (
          item.top_distributors.map((d) => (
            <Box key={d.distributor_code ?? '∅'} sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 0.3,
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary }}>
                  {d.distributor_code ?? '—'}
                  <Box
                    component="span"
                    sx={{ fontSize: 10.5, color: colors.textMuted, fontWeight: 500, ml: 0.5 }}
                  >
                    {d.account_count} accts
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum"',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmt6PkNumber(Number(d.cases_9l))} 6-pk
                  <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                    {(d.share * 100).toFixed(1)}%
                  </Box>
                </Typography>
              </Box>
              <MiniBar share={d.share} color={presentation.color} />
            </Box>
          ))
        )}
      </Box>

      {/* Trajectory + state facts */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          {sectionTitle('Recent vs Prior 3M')}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                Recent
              </Box>
              <Box sx={{ flex: 1 }}>
                <MiniBar share={recent3m / maxBaselineWidth} color={presentation.color} />
              </Box>
              <Box
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  fontFeatureSettings: '"tnum"',
                  minWidth: 60,
                  textAlign: 'right',
                }}
              >
                {fmt6PkNumber(recent3m)} 6-pk
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                Prior
              </Box>
              <Box sx={{ flex: 1 }}>
                <MiniBar share={prior3m / maxBaselineWidth} color={colors.textMuted} />
              </Box>
              <Box
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: colors.textSecondary,
                  fontFeatureSettings: '"tnum"',
                  minWidth: 60,
                  textAlign: 'right',
                }}
              >
                {fmt6PkNumber(prior3m)} 6-pk
              </Box>
            </Box>
          </Box>
        </Box>

        <Box>
          {sectionTitle('Recent vs Prior 12M (YoY)')}
          {(() => {
            const maxYoy = Math.max(recent12m, prior12m, 1);
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                    Recent
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <MiniBar share={recent12m / maxYoy} color={presentation.color} />
                  </Box>
                  <Box
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: colors.textPrimary,
                      fontFeatureSettings: '"tnum"',
                      minWidth: 70,
                      textAlign: 'right',
                    }}
                  >
                    {fmt6PkNumber(recent12m)} 6-pk
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                    Prior
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <MiniBar share={prior12m / maxYoy} color={colors.textMuted} />
                  </Box>
                  <Box
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: colors.textSecondary,
                      fontFeatureSettings: '"tnum"',
                      minWidth: 70,
                      textAlign: 'right',
                    }}
                  >
                    {fmt6PkNumber(prior12m)} 6-pk
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Box>

        <Box>
          {sectionTitle('State facts')}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '4px 12px',
              fontSize: 11.5,
              alignItems: 'baseline',
            }}
          >
            <span style={{ color: colors.textMuted }}>Active rhythm</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {item.months_active_12m} / 12 months
              <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                · {rhythmLabel}
              </Box>
            </span>
            {peakMonthName && (
              <>
                <span style={{ color: colors.textMuted }}>Peak month</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {peakMonthLabel}
                  <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                    · {fmt6PkNumber(Number(item.peak_month_9l))} 6-pk · Peaks in {peakMonthName}
                  </Box>
                </span>
              </>
            )}
            <span style={{ color: colors.textMuted }}>Acct momentum 90d</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              <Box component="span" sx={{ color: colors.success, fontWeight: 700 }}>
                +{item.accounts_gained_90d}
              </Box>
              {' / '}
              <Box component="span" sx={{ color: colors.error, fontWeight: 700 }}>
                −{item.accounts_churned_90d}
              </Box>
              <Box
                component="span"
                sx={{
                  color: net90d > 0 ? colors.success : net90d < 0 ? colors.error : colors.textMuted,
                  fontWeight: 700,
                  ml: 0.5,
                }}
              >
                ({net90d > 0 ? '+' : ''}
                {net90d} net)
              </Box>
            </span>
            <span style={{ color: colors.textMuted }}>Total volume</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(volume)} 6-pk · {num.format(volume)} 9L
              {physical > 0 && (
                <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                  · {num.format(physical)} physical
                </Box>
              )}
            </span>
            <span style={{ color: colors.textMuted }}>Avg / account</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(avgPerAccount)} 6-pk
              <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                · median {num.format(portfolioMedians.depth)}
              </Box>
            </span>
            <span style={{ color: colors.textMuted }}>Lifecycle</span>
            <span style={{ fontWeight: 600 }}>
              {item.first_active ? shortMonth(item.first_active) : '—'} →{' '}
              {item.last_active ? shortMonth(item.last_active) : '—'}
              {daysSinceLast !== null && (
                <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                  · {daysSinceLast}d ago
                </Box>
              )}
            </span>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------
// Toolbar menu
// ----------------------------------------------------------------

interface ToolbarMenuProps {
  label: string;
  value: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function ToolbarMenu({ label, value, open, onToggle, children }: ToolbarMenuProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, onToggle, open);
  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.6,
          borderRadius: 1,
          border: `1px solid ${open ? colors.chartPrimary : colors.border}`,
          bgcolor: open ? `${colors.chartPrimary}08` : '#fff',
          fontSize: 12,
          fontWeight: 500,
          color: colors.textPrimary,
          transition: 'border-color 0.12s, background-color 0.12s',
          '&:hover': { borderColor: colors.chartPrimary },
        }}
      >
        <Box component="span" sx={{ color: colors.textMuted, fontSize: 11 }}>
          {label}:
        </Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {value}
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        )}
      </Box>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 10,
            minWidth: 240,
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 28px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.05)',
            overflow: 'hidden',
            py: 0.5,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

interface MenuRowProps {
  selected: boolean;
  primary: string;
  secondary?: string;
  onClick: () => void;
}

function MenuRow({ selected, primary, secondary, onClick }: MenuRowProps) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        display: 'block',
        width: '100%',
        px: 1.5,
        py: 0.9,
        textAlign: 'left',
        bgcolor: selected ? `${colors.chartPrimary}10` : 'transparent',
        '&:hover': { bgcolor: selected ? `${colors.chartPrimary}18` : colors.surfaceHover },
      }}
    >
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: selected ? 700 : 500,
          color: selected ? colors.chartPrimary : colors.textPrimary,
          lineHeight: 1.3,
        }}
      >
        {primary}
      </Typography>
      {secondary && (
        <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.15 }}>
          {secondary}
        </Typography>
      )}
    </Box>
  );
}

// PremisesMiniBar — compact stacked horizontal bar showing this state's
// premises mix at a glance. Segments are width-proportional to account
// count, colored to match PremisesBadge (success for ON, info for OFF,
// warning for NA, muted for unknown). Hover surfaces the raw counts.
// Renders nothing when the state has zero accounts.
interface PremisesMiniBarProps {
  counts: { on: number; off: number; na: number; unknown: number };
}

function PremisesMiniBar({ counts }: PremisesMiniBarProps) {
  const total = counts.on + counts.off + counts.na + counts.unknown;
  if (total <= 0) return null;
  const segments: Array<{ key: string; n: number; color: string; label: string }> = [
    { key: 'on', n: counts.on, color: PREMISES_STYLE.ON!.color, label: 'ON' },
    { key: 'off', n: counts.off, color: PREMISES_STYLE.OFF!.color, label: 'OFF' },
    { key: 'na', n: counts.na, color: PREMISES_STYLE.NA!.color, label: 'NA' },
    { key: 'unknown', n: counts.unknown, color: colors.textMuted, label: 'Unknown' },
  ];
  return (
    <Tooltip
      placement="top"
      maxWidth={240}
      title={
        <Box sx={{ p: 0.25, minWidth: 160 }}>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: colors.textMuted,
              mb: 0.4,
            }}
          >
            Premises mix
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto auto auto',
              gap: '4px 10px',
              fontSize: 11.5,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {segments.map((s) => (
              <Box key={s.key} sx={{ display: 'contents' }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: s.color,
                    alignSelf: 'center',
                  }}
                />
                <span style={{ color: colors.textSecondary }}>{s.label}</span>
                <span style={{ fontWeight: 700, textAlign: 'right' }}>
                  {s.n} ({Math.round((s.n / total) * 100)}%)
                </span>
              </Box>
            ))}
          </Box>
        </Box>
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          height: 5,
          width: 60,
          borderRadius: 3,
          overflow: 'hidden',
          cursor: 'help',
          flexShrink: 0,
          border: `1px solid ${colors.borderLight}`,
        }}
      >
        {segments.map((s) =>
          s.n > 0 ? (
            <Box
              key={s.key}
              sx={{
                width: `${(s.n / total) * 100}%`,
                bgcolor: s.color,
                opacity: 0.85,
              }}
            />
          ) : null,
        )}
      </Box>
    </Tooltip>
  );
}

// Section divider rendered when the control/license boundary appears
// in the paginated state list. Shows a small label + subtotal of how
// many states sit in this group on the current page.
interface GeoSectionHeaderProps {
  classification: 'control' | 'license';
  states: { state_code: string; cases_9l: string; account_count: number }[];
}

function GeoSectionHeader({ classification, states }: GeoSectionHeaderProps) {
  const sameClass = states.filter((s) => classifyState(s.state_code) === classification);
  const totalVol = sameClass.reduce((sum, s) => sum + Number(s.cases_9l), 0);
  const totalAccts = sameClass.reduce((sum, s) => sum + s.account_count, 0);
  const isControl = classification === 'control';
  const accent = isControl ? '#7c3aed' : colors.textMuted;
  const eyebrow = isControl ? 'Control states' : 'License states';
  const subline = isControl
    ? 'State board is the buyer — pitch listings + promo approvals.'
    : 'Distributor / retail outlet sell-through — relationship work.';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1.25,
        flexWrap: 'wrap',
        px: 1.5,
        py: 1,
        mt: 1.25,
        mb: 0.5,
        bgcolor: isControl ? `${accent}08` : colors.surfaceSubtle,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 0.75,
      }}
    >
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: accent,
        }}
      >
        {eyebrow}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, flex: 1, minWidth: 0 }}>
        {subline}
      </Typography>
      <Typography sx={{ fontSize: 11, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
        {sameClass.length} state{sameClass.length === 1 ? '' : 's'} on page · {totalAccts} accounts
        · {Math.round(totalVol * 2)} 6-pk · {Math.round(totalVol)} 9L
      </Typography>
    </Box>
  );
}

// ----------------------------------------------------------------
// View-mode toggle — narrows the section to all / control / license.
// ----------------------------------------------------------------

interface ViewModeToggleProps {
  mode: ViewMode;
  counts: { all: number; control: number; license: number };
  onChange: (next: ViewMode) => void;
}

function ViewModeToggle({ mode, counts, onChange }: ViewModeToggleProps) {
  const options: { value: ViewMode; label: string; count: number; color: string }[] = [
    { value: 'all', label: 'All', count: counts.all, color: colors.chartPrimary },
    { value: 'control', label: 'Control', count: counts.control, color: '#7c3aed' },
    { value: 'license', label: 'License', count: counts.license, color: colors.textMuted },
  ];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0,
        bgcolor: colors.surfaceSubtle,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 1.5,
        p: 0.3,
        flexShrink: 0,
      }}
    >
      {options.map((opt) => {
        const active = mode === opt.value;
        return (
          <Box
            key={opt.value}
            component="button"
            type="button"
            onClick={() => onChange(opt.value)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.4,
              px: 1.1,
              py: 0.5,
              border: 'none',
              background: active ? '#fff' : 'transparent',
              cursor: 'pointer',
              borderRadius: 1.25,
              fontSize: 11.5,
              fontWeight: active ? 700 : 600,
              color: active ? opt.color : colors.textSecondary,
              letterSpacing: '0.02em',
              boxShadow: active ? '0 1px 2px rgba(15,23,42,0.06)' : 'none',
              transition: 'all 0.12s',
              fontFeatureSettings: '"tnum"',
              '&:hover': active
                ? {}
                : { color: colors.textPrimary, background: colors.surfaceHover },
            }}
          >
            {opt.label}
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: active ? opt.color : colors.textMuted,
                bgcolor: active ? `${opt.color}14` : 'transparent',
                px: 0.4,
                py: 0.05,
                borderRadius: 0.5,
                lineHeight: 1.3,
              }}
            >
              {opt.count}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
